/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Autocomplete,
  Chip,
  Box,
  FormHelperText,
  FormLabel,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RichTextEditor from 'components/RichTextEditor';
import { ISO_CURRENCIES, formatCurrencyOption } from 'components/SettingsComponents/customObjectsFieldTypes';
import { uploadPrivateObject, uploadPublicObject } from 'utils/S3';
import {
  inferImageIsPublic,
  withBatchPrivacy,
} from 'utils/customObjectImagePrivacy';

function RelationInput({ field, value, onChange, axiosInstance, disabled }) {
  const [options, setOptions] = useState([]);
  const target = field.referencedObject || 'lead';
  const endpoint = target.startsWith('co_')
    ? `/custom-objects/${target.replace(/^co_/, '')}/data?limit=100`
    : `/${target}s/?limit=100`;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(endpoint);
        const rows = Array.isArray(res.data) ? res.data : res.data?.items || [];
        if (active) setOptions(rows);
      } catch (e) {
        if (active) setOptions([]);
      }
    })();
    return () => { active = false; };
  }, [axiosInstance, endpoint, target]);

  const getLabel = (row) => {
    const namedValue =
      row.full_lead_name ||
      row.island_name ||
      row.name ||
      row.title ||
      row.subject ||
      row.email ||
      Object.entries(row).find(
        ([key, val]) =>
          key.endsWith('_name') &&
          typeof val === 'string' &&
          val.trim()
      )?.[1];
    return namedValue ? `${namedValue} (#${row.id})` : `#${row.id}`;
  };

  if (field.type === 'multirelation') {
    const selected = options.filter((o) => (Array.isArray(value) ? value : []).includes(o.id));
    return (
      <Autocomplete
        multiple
        disabled={disabled}
        options={options}
        getOptionLabel={getLabel}
        value={selected}
        onChange={(_, vals) => onChange(vals.map((v) => v.id))}
        renderInput={(params) => <TextField {...params} label={field.label} />}
      />
    );
  }

  const selected = options.find((o) => o.id === value) || null;
  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      getOptionLabel={getLabel}
      value={selected}
      onChange={(_, val) => onChange(val?.id ?? null)}
      renderInput={(params) => <TextField {...params} label={field.label} required={field.required} />}
    />
  );
}

function ImageFieldInput({
  field,
  value,
  onChange,
  axiosInstance,
  disabled,
  error,
  multiple = false,
  featurePath = 'custom-objects/images',
}) {
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [isPublic, setIsPublic] = useState(() => inferImageIsPublic(value));
  const inputId = `co-image-${field.name}`;
  const normalizedValue =
    typeof value === 'string' && value.trim()
      ? { url: value.trim() }
      : value;
  const images = multiple
    ? (Array.isArray(normalizedValue) ? normalizedValue : normalizedValue ? [normalizedValue] : [])
    : (normalizedValue ? [normalizedValue] : []);
  const persistUrlOnly = field.name === 'page_image';

  useEffect(() => {
    setIsPublic(inferImageIsPublic(value));
  }, [value]);

  const applyPrivacy = (nextPublic) => {
    setIsPublic(nextPublic);
    if (multiple && Array.isArray(value) && value.length) {
      onChange(withBatchPrivacy(value, nextPublic));
    }
  };

  const handleFiles = async (filesInput) => {
    const files = Array.from(filesInput || []);
    if (!files.length || disabled) return;
    const invalidType = files.find((file) => !file.type?.startsWith('image/'));
    if (invalidType) {
      setLocalError(`${invalidType.name}: only image files are allowed`);
      return;
    }
    const oversized = files.find((file) => file.size > 100 * 1024 * 1024);
    if (oversized) {
      setLocalError(`${oversized.name}: image must be at most 100MB`);
      return;
    }

    // Comic multi-select stays entirely in the browser until the user saves.
    // Object URLs provide previews without creating unwanted R2 objects.
    if (multiple) {
      const stagedImages = files.map((file) => ({
        file,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        url: URL.createObjectURL(file),
        is_local_preview: true,
        is_public: isPublic,
      }));
      setLocalError(null);
      onChange(withBatchPrivacy([...images, ...stagedImages], isPublic));
      return;
    }

    if (!axiosInstance) {
      setLocalError('Upload is unavailable');
      return;
    }

    setLocalError(null);
    setUploading(true);
    try {
      const file = files[0];
      const uploadFn = isPublic ? uploadPublicObject : uploadPrivateObject;
      const uploaded = await uploadFn({
        axiosInstance,
        file,
        featurePath,
        scope: 'org',
        includeDatePartition: true,
      });
      if (persistUrlOnly) {
        onChange(uploaded.url);
      } else {
        onChange({
          bucket_name: uploaded.bucket,
          storage_path: uploaded.path,
          file_name: file.name,
          file_type: uploaded.contentType || file.type,
          file_size: uploaded.size || file.size,
          url: uploaded.url,
          is_public: isPublic,
        });
      }
    } catch (err) {
      setLocalError(err?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const removed = images[index];
    if (removed?.is_local_preview && removed.url) {
      URL.revokeObjectURL(removed.url);
    }
    if (!multiple) {
      onChange(null);
      return;
    }
    onChange(images.filter((_, imageIndex) => imageIndex !== index));
  };

  const moveImage = (index, direction) => {
    const destination = index + direction;
    if (destination < 0 || destination >= images.length) return;
    const reordered = [...images];
    [reordered[index], reordered[destination]] = [
      reordered[destination],
      reordered[index],
    ];
    onChange(reordered);
  };

  const helper = localError || error || field.helpText;

  return (
    <Box sx={{ mt: 0.5, mb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <FormLabel
          required={!!field.required}
          error={!!(localError || error)}
          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
        >
          {field.label || field.name}
        </FormLabel>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={isPublic}
              onChange={(e) => applyPrivacy(e.target.checked)}
              disabled={disabled || uploading}
            />
          )}
          label={isPublic ? 'Public upload' : 'Private upload'}
          sx={{ mr: 0, '.MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {isPublic
          ? 'Public images use the permanent CDN URL (non-expiring).'
          : 'Private images stay in the private bucket and use signed URLs.'}
      </Typography>

      {images.length ? (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 1.5,
              mb: 1.5,
            }}
          >
            {images.map((image, index) => (
              <Box
                key={`${image.storage_path || image.file_name}-${index}`}
                sx={{
                  position: 'relative',
                  border: '1px solid',
                  borderColor: index === 0 ? 'primary.main' : 'grey.300',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                {multiple && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 1,
                      minWidth: 28,
                      height: 28,
                      px: 0.75,
                      borderRadius: '999px',
                      bgcolor: index === 0 ? 'primary.main' : 'rgba(15, 23, 42, 0.82)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      boxShadow: '0 2px 6px rgba(15, 23, 42, 0.35)',
                    }}
                  >
                    {index + 1}
                  </Box>
                )}
                <Box
                  component="img"
                  src={image.url}
                  alt={image.file_name || `${field.label} ${index + 1}`}
                  sx={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                />
                <Box sx={{ px: 1, py: 0.75 }}>
                  <Typography
                    variant="caption"
                    fontWeight={index === 0 ? 600 : 400}
                    sx={{ display: 'block' }}
                    noWrap
                  >
                    {index === 0 && multiple ? 'Cover · ' : ''}
                    {image.file_name || `Page ${index + 1}`}
                  </Typography>
                  {multiple && (
                    <Typography variant="caption" color="text.secondary">
                      Position {index + 1}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, pb: 0.5 }}>
                  {multiple && (
                    <Box>
                      <Button
                        size="small"
                        disabled={disabled || index === 0}
                        onClick={() => moveImage(index, -1)}
                        sx={{ minWidth: 28, p: 0.25 }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </Button>
                      <Button
                        size="small"
                        disabled={disabled || index === images.length - 1}
                        onClick={() => moveImage(index, 1)}
                        sx={{ minWidth: 28, p: 0.25 }}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </Button>
                    </Box>
                  )}
                  <Button
                    color="error"
                    size="small"
                    disabled={disabled || uploading}
                    onClick={() => removeImage(index)}
                    sx={{ minWidth: 28, p: 0.25, ml: 'auto' }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          <Button
            component="label"
            variant="outlined"
            color="info"
            size="small"
            disabled={disabled || uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
            sx={{
              color: 'info.main',
              borderColor: 'info.main',
              fontWeight: 600,
              '&:hover': {
                color: 'info.dark',
                borderColor: 'info.dark',
                bgcolor: 'rgba(2, 136, 209, 0.08)',
              },
            }}
          >
            {multiple ? 'Add more pages' : 'Replace image'}
            <input
              id={inputId}
              hidden
              type="file"
              accept="image/*"
              multiple={multiple}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = '';
                handleFiles(files);
              }}
            />
          </Button>
        </Box>
      ) : (
        <Box
          component="label"
          htmlFor={inputId}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed',
            borderColor: localError || error ? 'error.main' : 'grey.300',
            borderRadius: 1,
            p: 2.5,
            cursor: disabled || uploading ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            minHeight: 120,
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled || uploading) return;
            handleFiles(e.dataTransfer.files);
          }}
        >
          <input
            id={inputId}
            hidden
            type="file"
            accept="image/*"
            multiple={multiple}
            disabled={disabled || uploading}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              handleFiles(files);
            }}
          />
          {uploading ? (
            <CircularProgress size={28} />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
              <Typography variant="body2" color="primary">
                {multiple ? 'Upload comic pages' : 'Upload image'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {multiple
                  ? 'Select multiple images; numbered badges show page order (max 100MB each)'
                  : 'or drag and drop (max 100MB)'}
              </Typography>
              {multiple && (
                <Typography variant="caption" color="text.secondary">
                  Images remain in this browser until you click Save
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {helper ? (
        <FormHelperText error={!!(localError || error)} sx={{ mx: 0.5 }}>
          {helper}
        </FormHelperText>
      ) : null}
    </Box>
  );
}

export default function CustomObjectFieldRenderer({
  field,
  value,
  onChange,
  disabled = false,
  axiosInstance,
  error = null,
  multipleImages = false,
  featurePath = 'custom-objects/images',
}) {
  const common = {
    fullWidth: true,
    disabled,
    label: field.label,
    required: !!field.required,
    helperText: error || field.helpText,
    error: !!error,
    placeholder: field.placeholder,
    margin: 'dense',
  };

  const textMaxLength = field.maxLength || (field.type === 'text' ? 25 : field.type === 'email' ? 255 : 2048);

  switch (field.type) {
    case 'text':
      return (
        <TextField
          {...common}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ maxLength: Math.min(field.maxLength || 25, 25) }}
        />
      );
    case 'long_text':
      return (
        <Box sx={{ mt: 1, mb: 1 }}>
          <FormLabel
            required={!!field.required}
            error={!!error}
            sx={{ display: 'block', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}
          >
            {field.label || field.name}
          </FormLabel>
          <RichTextEditor
            value={value ?? ''}
            onChange={onChange}
            readOnly={disabled}
            height={140}
            resizable
            showAiGenerate={false}
          />
          {(error || field.helpText) && (
            <FormHelperText error={!!error} sx={{ mx: 1.75 }}>
              {error || field.helpText}
            </FormHelperText>
          )}
        </Box>
      );
    case 'number':
    case 'decimal':
      return (
        <TextField
          {...common}
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'currency': {
      const currencyValue = typeof value === 'object' && value ? value : { amount: value ?? '', currencyCode: field.currencyCode || 'USD' };
      return (
        <Box display="flex" gap={1}>
          <TextField
            {...common}
            type="number"
            label={`${field.label} amount`}
            value={currencyValue.amount ?? ''}
            onChange={(e) => onChange({ ...currencyValue, amount: e.target.value === '' ? null : Number(e.target.value) })}
            sx={{ flex: 2 }}
          />
          <TextField
            select
            label="Currency"
            value={currencyValue.currencyCode || 'USD'}
            onChange={(e) => onChange({ ...currencyValue, currencyCode: e.target.value })}
            sx={{ flex: 1 }}
            margin="dense"
            disabled={disabled}
          >
            {ISO_CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>{formatCurrencyOption(c)}</MenuItem>
            ))}
          </TextField>
        </Box>
      );
    }
    case 'boolean':
    case 'checkbox': {
      const trueLabel = field.trueLabel || 'Yes';
      const falseLabel = field.falseLabel || 'No';
      const display = field.type === 'checkbox'
        ? 'checkbox'
        : (field.booleanDisplay || 'switch');

      if (display === 'dropdown') {
        return (
          <TextField
            {...common}
            select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => {
              if (e.target.value === '') onChange(null);
              else onChange(e.target.value === 'true');
            }}
          >
            <MenuItem value="true">{trueLabel}</MenuItem>
            <MenuItem value="false">{falseLabel}</MenuItem>
          </TextField>
        );
      }

      if (display === 'checkbox') {
        return (
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                disabled={disabled}
              />
            )}
            label={`${field.label}${value ? ` (${trueLabel})` : ` (${falseLabel})`}`}
          />
        );
      }

      return (
        <FormControlLabel
          control={(
            <Switch
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
          )}
          label={`${field.label}: ${value ? trueLabel : falseLabel}`}
        />
      );
    }
    case 'url':
    case 'email':
      return (
        <TextField
          {...common}
          type={field.type === 'email' ? 'email' : 'text'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ maxLength: textMaxLength }}
        />
      );
    case 'date':
      return (
        <TextField
          {...common}
          type="date"
          InputLabelProps={{ shrink: true }}
          value={value ? String(value).slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'datetime':
      return (
        <TextField
          {...common}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={value ? String(value).slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    case 'time':
      return (
        <TextField
          {...common}
          type="time"
          InputLabelProps={{ shrink: true }}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ step: 1 }}
        />
      );
    case 'dropdown':
      return (
        <TextField {...common} select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          {(field.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      );
    case 'select_list':
      return (
        <Autocomplete
          multiple
          disabled={disabled}
          options={field.options || []}
          value={Array.isArray(value) ? value : []}
          onChange={(_, vals) => onChange(vals)}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => <TextField {...params} label={field.label} required={field.required} margin="dense" />}
        />
      );
    case 'list_of_texts': {
      const delimiter = field.delimiter || ',';
      const textValue = Array.isArray(value) ? value.join(`${delimiter} `) : (value ?? '');
      return (
        <TextField
          {...common}
          value={textValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Values separated by "${delimiter}"`}
        />
      );
    }
    case 'image':
      return (
        <ImageFieldInput
          field={field}
          value={value}
          onChange={onChange}
          axiosInstance={axiosInstance}
          disabled={disabled}
          error={error}
          multiple={multipleImages}
          featurePath={featurePath}
        />
      );
    case 'relation':
    case 'multirelation':
      return (
        <RelationInput field={field} value={value} onChange={onChange} axiosInstance={axiosInstance} disabled={disabled} />
      );
    default:
      return (
        <TextField {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
  }
}
